import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { formatToLocalTime } from '../../utils/dateUtils'
import { 
  ArrowLeftIcon,
  PaperAirplaneIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  StopIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

import { interviewsApi } from '../../services/api'
import {
  requestInterviewDisplayMedia,
  validateInterviewScreenShareAsync,
  stopMediaStream,
  isScreenShareCancelledError,
  conductShouldRequireScreenShare,
  normalizeInterviewSessionStatus,
  isDisplayCaptureAvailable,
  type InterviewScreenShareIssue,
} from '../../utils/interviewScreenShare'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface InterviewQuestion {
  question_id: string
  question_text: string
  question_type: string
  difficulty_level: string
  skill_focus: string[]
  expected_duration_minutes: number
  evaluation_criteria: string[]
  strategy_reasoning?: string
  expected_insights: string[]
  potential_follow_ups: string[]
}

interface InterviewSession {
  id: string
  session_id: string
  status: string
  interview_type: string
  max_questions: number
  target_duration_minutes: number
  started_at?: string
  job_id: string
  resume_id: string
}

interface Message {
  id: string
  type: 'question' | 'response'
  content: string
  timestamp: Date
  metadata?: any
}

const InterviewConductPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [currentResponse, setCurrentResponse] = useState<string>('')
  const [isResponding, setIsResponding] = useState<boolean>(false)
  const [responseStartTime, setResponseStartTime] = useState<Date | null>(null)
  const [interviewStartTime, setInterviewStartTime] = useState<Date | null>(null)
  const [currentQuestion, setCurrentQuestion] = useState<InterviewQuestion | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  /** Entire-screen + system-audio capture is mandatory; stream kept alive for the session. */
  const [screenShareReady, setScreenShareReady] = useState(false)
  const [screenGateMode, setScreenGateMode] = useState<'required' | 'wrong_choice'>('required')
  const [lastShareIssue, setLastShareIssue] = useState<InterviewScreenShareIssue | null>(null)
  const screenStreamRef = useRef<MediaStream | null>(null)

  // Fetch interview session details
  const { data: session, isLoading: sessionLoading } = useQuery<InterviewSession>({
    queryKey: ['interview-session', sessionId],
    queryFn: async () => {
      const response = await interviewsApi.getSessionDetails(sessionId!)
      return response.data.session
    },
    enabled: !!sessionId,
    refetchInterval: 5000 // Refresh every 5 seconds to get updates
  })

  // Submit response mutation
  const submitResponseMutation = useMutation({
    mutationFn: async (data: { response_text: string; response_time_seconds: number }) => {
      const response = await interviewsApi.submitResponse(sessionId!, data)
      return response.data
    },
    onSuccess: (data) => {
      // Add response to messages
      const responseMessage: Message = {
        id: `response_${Date.now()}`,
        type: 'response',
        content: currentResponse,
        timestamp: new Date(),
        metadata: { response_id: data.response_id }
      }
      setMessages(prev => [...prev, responseMessage])

      // Check if interview is complete
      if (data.interview_complete) {
        handleInterviewComplete()
      } else if (data.next_question) {
        // Add next question to messages
        const questionMessage: Message = {
          id: `question_${Date.now()}`,
          type: 'question',
          content: data.next_question.question_text,
          timestamp: new Date(),
          metadata: data.next_question
        }
        setMessages(prev => [...prev, questionMessage])
        setCurrentQuestion(data.next_question)
        setResponseStartTime(new Date())
      }

      setCurrentResponse('')
      setIsResponding(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to submit response')
      setIsResponding(false)
    }
  })

  // Complete interview mutation
  const completeInterviewMutation = useMutation({
    mutationFn: async () => {
      const response = await interviewsApi.completeInterview(sessionId!)
      return response.data
    },
    onSuccess: () => {
      toast.success('Interview completed successfully!')
      navigate(`/interviews/${sessionId}`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to complete interview')
    }
  })

  // Initialize interview on component mount
  useEffect(() => {
    const st = normalizeInterviewSessionStatus(session?.status)
    if (session && st === 'not_started') {
      fetchFirstQuestion()
    } else if (session && st === 'in_progress') {
      loadExistingMessages()
    }
  }, [session])

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const fetchFirstQuestion = async () => {
    try {
      // This would typically be handled by the create session endpoint
      // For now, we'll simulate getting the first question
      setInterviewStartTime(new Date())
      setResponseStartTime(new Date())
    } catch (error) {
      console.error('Failed to fetch first question:', error)
      toast.error('Failed to start interview')
    }
  }

  const loadExistingMessages = async () => {
    try {
      // In a real implementation, we'd fetch the existing conversation
      // For now, we'll initialize with a welcome message
      if (messages.length === 0) {
        const welcomeMessage: Message = {
          id: 'welcome',
          type: 'question',
          content: 'Welcome to your virtual interview! Let\'s begin with the first question.',
          timestamp: new Date(),
        }
        setMessages([welcomeMessage])
        setInterviewStartTime(new Date(session?.started_at || Date.now()))
        setResponseStartTime(new Date())
      }
    } catch (error) {
      console.error('Failed to load existing messages:', error)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const attachScreenShareEndedHandler = useCallback((stream: MediaStream) => {
    const vt = stream.getVideoTracks()[0]
    if (vt) {
      vt.onended = () => {
        toast.error('Screen sharing ended. Share your entire screen with system audio again to continue.')
        stopMediaStream(stream)
        if (screenStreamRef.current === stream) {
          screenStreamRef.current = null
        }
        setScreenShareReady(false)
        setScreenGateMode('required')
        setLastShareIssue(null)
      }
    }
  }, [])

  const requestInterviewScreenShare = useCallback(async () => {
    if (!isDisplayCaptureAvailable()) {
      toast.error(
        'Screen sharing is not available. Use a current browser (Chrome, Edge, Firefox, or Safari) and open the app over HTTPS (or localhost).'
      )
      return
    }
    try {
      const stream = await requestInterviewDisplayMedia()
      const validation = await validateInterviewScreenShareAsync(stream)

      if (!validation.ok) {
        stopMediaStream(stream)
        setLastShareIssue(validation.issue ?? 'not_entire_screen')
        setScreenGateMode('wrong_choice')
        return
      }

      stopMediaStream(screenStreamRef.current)
      screenStreamRef.current = stream
      attachScreenShareEndedHandler(stream)
      setLastShareIssue(null)
      setScreenShareReady(true)
      setScreenGateMode('required')
    } catch (err) {
      if (isScreenShareCancelledError(err)) {
        toast.error('Screen sharing is required to conduct this interview. Please click Share in the browser dialog.')
      } else {
        const msg = err instanceof Error ? err.message : 'Could not start screen sharing'
        toast.error(msg)
      }
    }
  }, [attachScreenShareEndedHandler])

  useEffect(() => {
    return () => {
      stopMediaStream(screenStreamRef.current)
      screenStreamRef.current = null
    }
  }, [])

  const handleSubmitResponse = (e: React.FormEvent) => {
    e.preventDefault()

    if (session && conductShouldRequireScreenShare(session.status) && !screenShareReady) {
      toast.error('Screen sharing with entire screen and system audio is required before you can submit answers.')
      return
    }
    
    if (!currentResponse.trim()) {
      toast.error('Please provide a response')
      return
    }

    const responseTime = responseStartTime 
      ? Math.round((new Date().getTime() - responseStartTime.getTime()) / 1000)
      : 0

    setIsResponding(true)
    
    submitResponseMutation.mutate({
      response_text: currentResponse.trim(),
      response_time_seconds: responseTime
    })
  }

  const handleInterviewComplete = () => {
    const completeMessage: Message = {
      id: 'complete',
      type: 'question',
      content: 'Thank you for completing the interview! We are now generating your assessment...',
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, completeMessage])
    
    // Complete the interview after a short delay
    setTimeout(() => {
      completeInterviewMutation.mutate()
    }, 2000)
  }

  const handleEndInterview = () => {
    if (window.confirm('Are you sure you want to end the interview early?')) {
      completeInterviewMutation.mutate()
    }
  }

  const formatDuration = (startTime: Date | null) => {
    if (!startTime) return '00:00'
    const now = new Date()
    const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000)
    const minutes = Math.floor(diff / 60)
    const seconds = diff % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  if (sessionLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">Interview Session Not Found</h3>
        <p className="text-red-600 mb-4">
          The interview session could not be found or has been deleted.
        </p>
        <button
          onClick={() => navigate('/interviews')}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Back to Interviews
        </button>
      </div>
    )
  }

  if (normalizeInterviewSessionStatus(session.status) === 'completed') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-green-800 mb-2">Interview Completed</h3>
        <p className="text-green-600 mb-4">
          This interview has already been completed.
        </p>
        <button
          onClick={() => navigate(`/interviews/${sessionId}`)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          View Results
        </button>
      </div>
    )
  }

  const needsScreenShareGate =
    !!session && conductShouldRequireScreenShare(session.status) && !screenShareReady

  return (
    <>
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/interviews')}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Virtual Interview - {session.session_id}
                </h1>
                <p className="text-sm text-gray-600">
                  {session.interview_type} interview
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Timer */}
            <div className="flex items-center text-sm text-gray-600">
              <ClockIcon className="w-4 h-4 mr-1" />
              {formatDuration(interviewStartTime)}
              {session.target_duration_minutes && (
                <span className="text-gray-400 ml-1">
                  / {session.target_duration_minutes}m
                </span>
              )}
            </div>
            
            {/* End Interview Button */}
            <button
              onClick={handleEndInterview}
              disabled={completeInterviewMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              <StopIcon className="w-4 h-4 mr-2" />
              End Interview
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'response' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl rounded-lg p-4 ${
                  message.type === 'question'
                    ? 'bg-blue-50 text-blue-900 border-l-4 border-blue-400'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                {message.type === 'question' && (
                  <div className="flex items-start mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-white text-sm font-medium">AI</span>
                    </div>
                    <div className="flex-1">
                      {message.metadata?.question_type && (
                        <div className="text-xs text-blue-600 mb-1">
                          {message.metadata.question_type.replace('_', ' ').toUpperCase()}
                          {message.metadata.skill_focus && message.metadata.skill_focus.length > 0 && (
                            <span className="ml-2">
                              Focus: {message.metadata.skill_focus.join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="text-sm">{message.content}</div>
                    </div>
                  </div>
                )}
                
                {message.type === 'response' && (
                  <div className="flex items-start">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">You</div>
                      <div className="text-sm">{message.content}</div>
                    </div>
                    <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center ml-3 flex-shrink-0">
                      <span className="text-white text-sm font-medium">U</span>
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-gray-400 mt-2">
                  {formatToLocalTime(message.timestamp.toISOString())}
                </div>
              </div>
            </div>
          ))}
          
          {isResponding && (
            <div className="flex justify-start">
              <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                <div className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span className="text-blue-900 text-sm">Processing your response...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Response Input */}
      {normalizeInterviewSessionStatus(session.status) === 'in_progress' &&
        screenShareReady &&
        !isResponding &&
        !completeInterviewMutation.isPending && (
        <div className="bg-white border-t border-gray-200 p-6">
          <form onSubmit={handleSubmitResponse} className="max-w-4xl mx-auto">
            <div className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={currentResponse}
                  onChange={(e) => setCurrentResponse(e.target.value)}
                  placeholder="Type your response here..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  disabled={submitResponseMutation.isPending}
                />
              </div>
              <button
                type="submit"
                disabled={!currentResponse.trim() || submitResponseMutation.isPending}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center self-end"
              >
                {submitResponseMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>

    {needsScreenShareGate && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 pointer-events-auto">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
          {screenGateMode === 'wrong_choice' ? (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                <InformationCircleIcon className="h-9 w-9 text-blue-600" aria-hidden />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Share Entire Screen with System Audio
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Please choose <strong>Entire screen</strong> and enable{' '}
                <strong>Also share system audio</strong>.
                {lastShareIssue === 'no_system_audio' && (
                  <span className="block mt-2 text-amber-700">
                    System audio was not shared. Turn on &quot;Also share system audio&quot; — you cannot use tab-only or window-only capture for this interview.
                  </span>
                )}
                {(lastShareIssue === 'not_entire_screen' || lastShareIssue === 'unknown_surface') && (
                  <span className="block mt-2 text-amber-700">
                    You selected a browser tab or window. For this interview you must share your entire screen — switching to Tab or Window is not allowed.
                  </span>
                )}
              </p>
              <ol className="text-left text-sm text-gray-700 space-y-2 mb-6 list-decimal list-inside">
                <li>
                  Click the <strong>Entire screen</strong> tab.
                </li>
                <li>Select the screen thumbnail.</li>
                <li>
                  Toggle <strong>Also share system audio</strong> ON.
                </li>
                <li>
                  Click <strong>Share</strong>.
                </li>
              </ol>
              <button
                type="button"
                onClick={() => void requestInterviewScreenShare()}
                className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
              >
                Try Again
              </button>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 ring-2 ring-amber-200">
                <ExclamationTriangleIcon className="h-9 w-9 text-amber-600" aria-hidden />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Screen Share Required</h2>
              <p className="text-sm text-gray-600 mb-6">
                You must share your <strong>entire screen</strong> with <strong>system audio</strong> to proceed. The browser will open its own dialog — select Entire screen, turn on system audio, then Share. You cannot complete the interview without this.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => void requestInterviewScreenShare()}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 ring-2 ring-blue-300"
                >
                  Open Share Dialog
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/interviews')}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gray-600 text-white font-medium hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    </>
  )
}

export default InterviewConductPage